import { Button } from "react-bootstrap";
import styled from "styled-components";

import {
  Card,
  CardGrid,
  FixedToolbarItem,
  FlexToolbarItem,
  Toolbar,
} from "@bagel-web/components";

import FunctionEditor from "./functionEditor/FunctionEditor";
import { functionNameError, newFunction } from "./playgroundFunctions";
import { FnDesc } from "../types";

// The step's shell reads as a surface behind the per-function cards.
const SurfaceCard = styled(Card)`
  background-color: var(--aa-surface-bg);
`;

/**
 * Step 3 — Functions: the run's function descriptions, edited in place.
 *
 * One FunctionEditor card per function — name, description, and its
 * arguments as recursive parameter blocks. This step owns the list: adding,
 * deleting, replacing an edited function, and judging each name against
 * its siblings.
 */
const PlaygroundFunctionsStep = ({
  functions,
  onFunctionsChange,
}: {
  functions: Array<FnDesc>;
  onFunctionsChange?: (functions: Array<FnDesc>) => void;
}) => {
  const updateFunction = (index: number, next: FnDesc) =>
    onFunctionsChange?.(
      functions.map((fn, candidate) => (candidate === index ? next : fn))
    );

  return (
    <CardGrid maxColumns={1}>
      <SurfaceCard>
        <Toolbar className="mb-3">
          <FlexToolbarItem>
            <div className="card-title h5 mb-0">Functions</div>
          </FlexToolbarItem>
          <FixedToolbarItem>
            <Button
              variant="secondary"
              onClick={() =>
                onFunctionsChange?.([newFunction(functions), ...functions])
              }
              data-testid="playground-function-add"
            >
              <i className="bi-plus-lg me-1" />
              Add Function
            </Button>
          </FixedToolbarItem>
        </Toolbar>
        {functions.length ? (
          <CardGrid maxColumns={1}>
            {functions.map((fn, index) => (
              <FunctionEditor
                key={index}
                index={index}
                fn={fn}
                nameError={functionNameError(functions, index)}
                onChange={(next) => updateFunction(index, next)}
                onDelete={() =>
                  onFunctionsChange?.(
                    functions.filter((_, candidate) => candidate !== index)
                  )
                }
              />
            ))}
          </CardGrid>
        ) : (
          <p className="text-body-secondary mb-0">No functions.</p>
        )}
      </SurfaceCard>
    </CardGrid>
  );
};

export default PlaygroundFunctionsStep;
